import { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import {
  PARTNER_CATEGORIES, PARTNER_SUBCATEGORIES, PARTNER_STATUSES, PARTNER_PRIORITIES,
  PARTNER_LANGS, pl, statusEntry, subcatEntry,
} from "../../lib/partnerConstants";
import PartnerDetailDialog from "./PartnerDetailDialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Handshake, Plus, Search, Download, Filter, Archive, ArchiveRestore, Trash2, Star, Phone, Mail, MapPin, Clock, AlertTriangle, Award, X } from "lucide-react";

const emptyNew = { name: "", category: "medical", subcategory: "", contact_name: "", function: "", phone: "", email: "", address: "", commune: "", language: "fr", strategic: false };

export default function PartnersTab({ readOnly }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "fr";
  const [data, setData] = useState({ partners: [], relation_editable: false });
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ category: "", subcategory: "", priority: "", status: "", language: "", commune: "", responsible: "", archived: "" });
  const [showFilters, setShowFilters] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [nf, setNf] = useState(emptyNew);
  const [dups, setDups] = useState([]);
  const [dupConfirmed, setDupConfirmed] = useState(false);

  const params = useMemo(() => {
    const p = { ...filters };
    if (q) p.q = q;
    Object.keys(p).forEach((k) => { if (!p[k]) delete p[k]; });
    return p;
  }, [filters, q]);

  const load = useCallback(async () => {
    try { const r = await api.get("/crm/partners", { params }); setData(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [params]);
  useEffect(() => { load(); }, [load]);

  const partners = data.partners || [];
  const relEditable = data.relation_editable;

  const exportCsv = async () => {
    try {
      const res = await api.get("/crm/partners/export.csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "partenaires.csv"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const checkDup = async (f) => {
    try { const r = await api.get("/crm/partners/check-duplicate", { params: { name: f.name, email: f.email, phone: f.phone, address: f.address } }); return r.data.duplicates || []; }
    catch { return []; }
  };

  const openNew = () => { setNf(emptyNew); setDups([]); setDupConfirmed(false); setNewOpen(true); };
  const createPartner = async () => {
    if (!nf.name.trim()) return toast.error(t("crm.partner.nameRequired"));
    if (!dupConfirmed) {
      const d = await checkDup(nf);
      if (d.length) { setDups(d); return; }
    }
    try {
      const r = await api.post("/crm/partners", nf);
      toast.success(t("crm.partner.saved")); setNewOpen(false); await load(); setDetailId(r.data.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleArchive = async (p) => {
    try { await api.post(`/crm/partners/${p.id}/archive`, { archived: !p.archived }); toast.success("OK"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (p) => {
    if (!window.confirm(t("crm.partner.confirmDelete"))) return;
    try { await api.delete(`/crm/partners/${p.id}`); toast.success(t("crm.partner.deleted")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const resetFilters = () => { setFilters({ category: "", subcategory: "", priority: "", status: "", language: "", commune: "", responsible: "", archived: "" }); setQ(""); };
  const today = new Date().toISOString().slice(0, 10);
  const subcatsFor = PARTNER_SUBCATEGORIES[filters.category] || [];

  return (
    <div className="space-y-4" data-testid="crm-partners-tab">
      {/* Barre d'actions */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px] sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder={t("crm.partner.searchPlaceholder")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="crm-partner-search" />
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowFilters((v) => !v)} data-testid="crm-partner-toggle-filters"><Filter className="h-4 w-4 mr-1" /> {t("crm.partner.filters")}</Button>
        <Button variant="outline" size="sm" onClick={exportCsv} data-testid="crm-partner-export"><Download className="h-4 w-4 mr-1" /> {t("crm.partner.export")}</Button>
        {!readOnly && <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-500" size="sm" data-testid="crm-add-partner"><Plus className="h-4 w-4 mr-1" /> {t("crm.partner.addBtn")}</Button>}
      </div>

      {!relEditable && <p className="text-xs text-amber-300/80 bg-amber-500/10 border border-amber-500/25 rounded-xl px-3 py-2" data-testid="partner-no-residence">{t("crm.partner.selectResidenceFirst")}</p>}

      {/* Filtres */}
      {showFilters && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="crm-partner-filters">
          <Select value={filters.category || "all"} onValueChange={(v) => setFilters({ ...filters, category: v === "all" ? "" : v, subcategory: "" })}>
            <SelectTrigger data-testid="filter-category"><SelectValue placeholder={t("crm.partner.allCategories")} /></SelectTrigger>
            <SelectContent><SelectItem value="all">{t("crm.partner.allCategories")}</SelectItem>{Object.keys(PARTNER_CATEGORIES).map((k) => <SelectItem key={k} value={k}>{pl(lang, PARTNER_CATEGORIES[k])}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.subcategory || "all"} onValueChange={(v) => setFilters({ ...filters, subcategory: v === "all" ? "" : v })} disabled={!filters.category}>
            <SelectTrigger data-testid="filter-subcategory"><SelectValue placeholder={t("crm.partner.allSubcategories")} /></SelectTrigger>
            <SelectContent><SelectItem value="all">{t("crm.partner.allSubcategories")}</SelectItem>{subcatsFor.map((s) => <SelectItem key={s.k} value={s.k}>{pl(lang, s)}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.priority || "all"} onValueChange={(v) => setFilters({ ...filters, priority: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-priority"><SelectValue placeholder={t("crm.partner.allPriorities")} /></SelectTrigger>
            <SelectContent><SelectItem value="all">{t("crm.partner.allPriorities")}</SelectItem>{Object.keys(PARTNER_PRIORITIES).map((k) => <SelectItem key={k} value={k}>{pl(lang, PARTNER_PRIORITIES[k])}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-status"><SelectValue placeholder={t("crm.partner.allStatuses")} /></SelectTrigger>
            <SelectContent><SelectItem value="all">{t("crm.partner.allStatuses")}</SelectItem>{PARTNER_STATUSES.map((s) => <SelectItem key={s.k} value={s.k}>{pl(lang, s)}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.language || "all"} onValueChange={(v) => setFilters({ ...filters, language: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-language"><SelectValue placeholder={t("crm.partner.allLanguages")} /></SelectTrigger>
            <SelectContent><SelectItem value="all">{t("crm.partner.allLanguages")}</SelectItem>{Object.keys(PARTNER_LANGS).map((k) => <SelectItem key={k} value={k}>{PARTNER_LANGS[k]}</SelectItem>)}</SelectContent>
          </Select>
          <Input placeholder={t("crm.partner.commune")} value={filters.commune} onChange={(e) => setFilters({ ...filters, commune: e.target.value })} data-testid="filter-commune" />
          <Input placeholder={t("crm.partner.responsible")} value={filters.responsible} onChange={(e) => setFilters({ ...filters, responsible: e.target.value })} data-testid="filter-responsible" />
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 cursor-pointer text-sm"><Switch checked={filters.archived === "1"} onCheckedChange={(v) => setFilters({ ...filters, archived: v ? "1" : "" })} data-testid="filter-archived" /> {t("crm.partner.showArchived")}</label>
            <Button variant="ghost" size="sm" onClick={resetFilters} className="ml-auto"><X className="h-3.5 w-3.5 mr-1" /> {t("crm.partner.resetFilters")}</Button>
          </div>
        </div>
      )}

      {/* Liste */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {partners.map((p) => {
          const st = statusEntry(p.relation?.status);
          const prio = PARTNER_PRIORITIES[p.relation?.priority];
          const overdue = p.relation?.relance_date && p.relation.relance_date <= today && !p.relation?.next_action_done;
          const sc = subcatEntry(p.category, p.subcategory);
          return (
            <div key={p.id} className="rounded-2xl border border-white/10 p-4 bg-white/[0.02] hover:border-emerald-400/30 transition cursor-pointer" data-testid={`crm-partner-${p.id}`} onClick={() => setDetailId(p.id)}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium truncate flex items-center gap-1.5"><Handshake className="h-4 w-4 text-emerald-400 shrink-0" /> {p.name} {p.strategic && <Star className="h-3.5 w-3.5 text-amber-400 shrink-0" />}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{pl(lang, PARTNER_CATEGORIES[p.category])}{sc ? " · " + pl(lang, sc) : (p.subcategory ? " · " + p.subcategory : "")}</div>
                </div>
                {prio && <span className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ color: prio.color, background: prio.color + "22" }}>{p.relation.priority}</span>}
              </div>

              <div className="mt-2 flex flex-wrap gap-1.5">
                {st && <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ color: st.color, background: st.color + "22" }}>{pl(lang, st)}</span>}
                {p.archived && <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold bg-white/10 text-white/60">{t("crm.partner.archivedBadge")}</span>}
              </div>

              <div className="mt-2 space-y-1 text-xs text-foreground/70">
                {p.commune && <div className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {p.commune}</div>}
                {p.phone && <div className="flex items-center gap-1"><Phone className="h-3 w-3" /> {p.phone}</div>}
              </div>

              {p.relation?.relance_date && !p.relation?.next_action_done && (
                <div className={`mt-2 text-[11px] flex items-center gap-1 ${overdue ? "text-rose-400" : "text-amber-400"}`}>
                  {overdue ? <AlertTriangle className="h-3 w-3" /> : <Clock className="h-3 w-3" />} {p.relation.next_action || t("crm.partner.nextAction")} · {p.relation.relance_date}
                </div>
              )}

              <div className="mt-3 flex items-center justify-between">
                <span className="inline-flex items-center gap-1 text-[11px] text-amber-300/80"><Award className="h-3 w-3" /> {p.counts?.orientations ?? 0} · {p.counts?.admissions ?? 0} adm.</span>
                {!readOnly && (
                  <span className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => toggleArchive(p)} title={p.archived ? t("crm.partner.unarchive") : t("crm.partner.archive")} data-testid={`crm-partner-archive-${p.id}`}>{p.archived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}</Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-400" onClick={() => del(p)} data-testid={`crm-partner-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </span>
                )}
              </div>
            </div>
          );
        })}
        {!partners.length && <p className="text-muted-foreground text-sm col-span-full py-8 text-center">{t("crm.partner.empty")}</p>}
      </div>

      {/* Création rapide */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{t("crm.partner.new")}</DialogTitle><DialogDescription className="sr-only">{t("crm.partner.new")}</DialogDescription></DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5"><Label>{t("crm.partner.organisation")}</Label><Input value={nf.name} onChange={(e) => { setNf({ ...nf, name: e.target.value }); setDupConfirmed(false); setDups([]); }} placeholder={t("crm.partner.namePlaceholder")} data-testid="new-partner-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>{t("crm.partner.category")}</Label>
                <Select value={nf.category} onValueChange={(v) => setNf({ ...nf, category: v, subcategory: "" })}>
                  <SelectTrigger data-testid="new-partner-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.keys(PARTNER_CATEGORIES).map((k) => <SelectItem key={k} value={k}>{pl(lang, PARTNER_CATEGORIES[k])}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>{t("crm.partner.subcategory")}</Label>
                <Select value={nf.subcategory || ""} onValueChange={(v) => setNf({ ...nf, subcategory: v })}>
                  <SelectTrigger data-testid="new-partner-subcategory"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{(PARTNER_SUBCATEGORIES[nf.category] || []).map((s) => <SelectItem key={s.k} value={s.k}>{pl(lang, s)}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>{t("crm.partner.contactName")}</Label><Input value={nf.contact_name} onChange={(e) => setNf({ ...nf, contact_name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>{t("crm.partner.phone")}</Label><Input value={nf.phone} onChange={(e) => { setNf({ ...nf, phone: e.target.value }); setDupConfirmed(false); }} /></div>
              <div className="space-y-1.5"><Label>{t("crm.partner.email")}</Label><Input value={nf.email} onChange={(e) => { setNf({ ...nf, email: e.target.value }); setDupConfirmed(false); }} /></div>
              <div className="space-y-1.5"><Label>{t("crm.partner.commune")}</Label><Input value={nf.commune} onChange={(e) => setNf({ ...nf, commune: e.target.value })} /></div>
              <div className="space-y-1.5 col-span-2"><Label>{t("crm.partner.address")}</Label><Input value={nf.address} onChange={(e) => { setNf({ ...nf, address: e.target.value }); setDupConfirmed(false); }} /></div>
              <div className="space-y-1.5"><Label>{t("crm.partner.language")}</Label>
                <Select value={nf.language} onValueChange={(v) => setNf({ ...nf, language: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.keys(PARTNER_LANGS).map((k) => <SelectItem key={k} value={k}>{PARTNER_LANGS[k]}</SelectItem>)}</SelectContent>
                </Select></div>
              <label className="flex items-center gap-2 cursor-pointer mt-6"><Switch checked={nf.strategic} onCheckedChange={(v) => setNf({ ...nf, strategic: v })} /><span className="text-sm">{t("crm.partner.strategic")}</span></label>
            </div>

            {dups.length > 0 && (
              <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-sm text-amber-200" data-testid="new-partner-dup-warning">
                <div className="font-semibold flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> {t("crm.partner.duplicateWarning")}</div>
                <ul className="mt-1 space-y-0.5 text-xs">{dups.map((d) => <li key={d.id}>• {d.name} <span className="text-amber-300/70">({t("crm.partner.duplicateBy")} {d.reasons.join(", ")})</span></li>)}</ul>
                <label className="flex items-center gap-2 mt-2 cursor-pointer"><Switch checked={dupConfirmed} onCheckedChange={setDupConfirmed} data-testid="new-partner-dup-confirm" /> <span className="text-xs">{t("crm.partner.saveAnyway")}</span></label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>{t("crm.actions.cancel")}</Button>
            <Button onClick={createPartner} className="bg-emerald-600 hover:bg-emerald-500" disabled={dups.length > 0 && !dupConfirmed} data-testid="new-partner-save">{t("crm.actions.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {detailId && <PartnerDetailDialog partnerId={detailId} open={!!detailId} onClose={() => setDetailId(null)} onChanged={load} relationEditable={relEditable} readOnly={readOnly} />}
    </div>
  );
}
